import {formatCertificateDate} from './transportAcceptanceDates';
import React, {useState,useEffect,useRef} from 'react';
import {createPortal} from 'react-dom';
import axios from 'axios';
import {Capacitor} from '@capacitor/core';
import {Browser} from '@capacitor/browser';
import {API_BASE_URL as API} from '../apiConfig';
import './TransportAcceptance.css';
import TransportAcceptance from './TransportAcceptance';

export default function TransportAcceptanceArchive({triggerClassName, triggerLabel = '承接书档案 · 批量导出'}) {
  const [open,setOpen] = useState(false);
  const [q,setQ] = useState('');
  const [month,setMonth] = useState('');
  const [driver,setDriver] = useState('');
  const [drivers,setDrivers] = useState([]);
  const [page,setPage] = useState(0);
  const [data,setData] = useState({items:[],total:0});
  const [selection,setSelection] = useState([]);
  const [history,setHistory] = useState([]);
  const [historyOrder,setHistoryOrder] = useState('');
  const [busy,setBusy] = useState(false);
  const [error,setError] = useState('');
  const [message,setMessage] = useState('');
  const [applied,setApplied] = useState({q:'',month:'',driver:''});
  const errorMessage = e => typeof e.response?.data?.detail === 'string' ? e.response.data.detail : '操作失败，请重试';
  const requestId = useRef(0);
  const load = async (p=0, filters={q,month,driver}) => {
    const id = ++requestId.current;
    setBusy(true); setError(''); setMessage('');
    setData({items:[],total:0}); setSelection([]); setHistory([]); setHistoryOrder('');
    try {
      const [result,options] = await Promise.all([
        axios.get(`${API}/admin/transport-acceptance/archive`,{params:{...filters,page:p}}),
        axios.get(`${API}/admin/transport-acceptance/drivers`,{params:{month:filters.month}}),
      ]);
      if (id !== requestId.current) return;
      setData(result.data); setDrivers(options.data); setPage(p); setApplied(filters); setSelection([]); setHistory([]); setHistoryOrder('');
    } catch(e) {if(id === requestId.current) setError(errorMessage(e));} finally {if(id === requestId.current) setBusy(false);}
  };
  useEffect(() => {
    if (!open) return;
    const counter = requestId;
    setBusy(true); setData({items:[],total:0}); setSelection([]); setHistory([]); setHistoryOrder('');
    const timer = setTimeout(() => load(0,{q,month,driver}), 200);
    return () => {clearTimeout(timer); ++counter.current;};
    // load receives this effect's complete filter snapshot.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open,q,month,driver]);
  const openDownload = async path => {
    const url = new URL(path,new URL(API,window.location.origin).origin).href;
    if(Capacitor.isNativePlatform()) await Browser.open({url});
    else {const a=document.createElement('a'); a.href=url; a.target='_blank'; a.rel='noopener noreferrer'; document.body.appendChild(a); a.click(); a.remove();}
  };
  const download = async v => {
    setBusy(true); setError('');
    try {const r=await axios.post(`${API}/orders/${v.order_id}/transport-acceptance/download-link`,{version_id:v.id}); await openDownload(r.data.url);}
    catch(e) {setError(errorMessage(e));} finally {setBusy(false);}
  };
  const batch = async selected => {
    setBusy(true); setError(''); setMessage('');
    try {
      const {data: result}=await axios.post(`${API}/admin/transport-acceptance/batch-export`,{...applied,version_ids:selected?selection:[]});
      await openDownload(result.url); setMessage(`正在将 ${result.count} 份承接书合并为一个 PDF。文件较多时请等待下载页面完成。`);
    } catch(e) {setError(errorMessage(e));} finally {setBusy(false);}
  };
  const changed=q!==applied.q||month!==applied.month||driver!==applied.driver;
  return <>
    <button type="button" className={triggerClassName || "transport-acceptance-trigger"} onClick={()=>setOpen(true)}>{triggerLabel}</button>
    {open&&createPortal(<div className="transport-acceptance-overlay">
      <section className="transport-acceptance-panel" role="dialog" aria-modal="true" aria-label="承接书档案">
        <header><div><small>SHONAN INTERNATIONAL</small><h2>運送引受書 · 档案</h2><p>按月份、司机筛选 · 合并为一个 PDF</p></div><button onClick={()=>setOpen(false)} aria-label="关闭档案">×</button></header>
        <main>
          <p className="ta-note">承接后自动签发，资料变化后自动归档新版。月份按月结订单生成日期、信用卡实际付款日期筛选（日本时间）；日期缺失的文件可在不选月份时查看，司机按当前配车筛选；订单已删除时使用签发时的司机记录。每单默认取最新已签发版本，未签发草稿不会混入正式 PDF。点击文件旁的“历史版本”可查看所有已签发副本。</p>
          <form onSubmit={e=>{e.preventDefault();load(0);}}>
            <div className="ta-grid">
              <label>归属月份<input type="month" value={month} onChange={e=>{setMonth(e.target.value);setDriver('');}}/></label>
              <label>司机<select value={driver} onChange={e=>setDriver(e.target.value)}><option value="">全部司机</option><option value="__unassigned__">未配车</option>{drivers.filter(d=>d._id).map(d=><option key={d._id} value={d._id}>{d.name}</option>)}</select></label>
              <label className="ta-wide">搜索<input placeholder="订单号、文件号或申请人名称" value={q} onChange={e=>setQ(e.target.value)}/></label>
            </div>
            <div className="ta-toolbar"><button disabled={busy} className="ta-primary">查询</button><button type="button" disabled={busy} onClick={()=>{setQ('');setMonth('');setDriver('');if(!q&&!month&&!driver) load(0,{q:'',month:'',driver:''});}}>清除筛选</button></div>
          </form>
          {error&&<p role="alert" className="ta-error">{error}</p>}
          {message&&<p role="status" className="ta-success">{message}</p>}
          {busy&&<p role="status" className="ta-note">正在加载筛选结果…</p>}
          {!changed&&<>
          <div className="ta-toolbar"><strong>共 {data.total} 份</strong><button disabled={busy||changed||!data.total} onClick={()=>batch(false)}>全部筛选结果 → 一个 PDF</button><button disabled={busy||changed||!selection.length} onClick={()=>batch(true)}>所选 {selection.length} 份 → 一个 PDF</button></div>
          {data.items.length>0&&<label><input type="checkbox" checked={data.items.every(v=>selection.includes(v.id))} onChange={e=>setSelection(e.target.checked?data.items.map(v=>v.id):[])}/>勾选本页</label>}
          {data.items.map(v=><article key={v.id} className="ta-version">
            <label><input type="checkbox" checked={selection.includes(v.id)} onChange={e=>setSelection(e.target.checked?[...selection,v.id]:selection.filter(id=>id!==v.id))}/><strong>{v.order_no} · {v.number}</strong></label>
            <p>{v.data.pickup_at?.replace('T',' ')} · {v.filter_driver_name||'未配车'} · {v.data.applicant_name}</p>
            <p>签发 {formatCertificateDate(v)} · 保存至少至 {v.retain_until || '长期保存（结束日期待补）'}（不会自动删除）</p>
            <div className="ta-toolbar"><button disabled={busy} onClick={()=>download(v)}>打开 PDF</button><button disabled={busy} onClick={async()=>{setBusy(true);setError('');try{const r=await axios.get(`${API}/admin/transport-acceptance/history`,{params:{order_id:v.order_id}});setHistory(r.data);setHistoryOrder(v.order_no);}catch(e){setError(errorMessage(e));}finally{setBusy(false);}}}>历史版本</button></div>
          </article>)}
          {historyOrder&&<fieldset><legend>{historyOrder} · 全部历史版本</legend>{history.map(v=><article className="ta-version" key={v.id}><strong>{v.number}</strong><p>{formatCertificateDate(v)} · 保存至少至 {v.retain_until || '长期保存（结束日期待补）'}</p><button disabled={busy} onClick={()=>download(v)}>打开此版本 PDF</button></article>)}</fieldset>}
          {!busy&&!data.total&&<p className="ta-note">此范围内没有已签发文件。有效订单正在自动签发，请稍后刷新。</p>}
          {!!data.pending_total&&<fieldset><legend>自动签发待处理 · {data.pending_total} 单</legend><p className="ta-note">系统自动生成 PDF，缺少内容留空；失败会自动重试。</p>{(data.pending||[]).map(v=><article key={v.id} className="ta-version"><strong>{v.order_no}</strong><p>{v.service_date} · {v.driver_name}</p><p>{v.automation_state==='retry'?'生成暂时失败，系统会自动重试':(v.automation_missing||[]).join('、')||'正在自动生成'}</p><TransportAcceptance order={v} admin/></article>)}</fieldset>}
          </>}
          <div className="ta-toolbar"><button disabled={busy||page===0||changed} onClick={()=>load(page-1,applied)}>上一页</button><span>{page+1}</span><button disabled={busy||(page+1)*30>=Math.max(data.total,data.pending_total||0)||changed} onClick={()=>load(page+1,applied)}>下一页</button></div>
        </main>
        <footer>按乘车日期排序 · 一次最多合并 1000 份 · 已签发副本独立保存</footer>
      </section>
    </div>,document.body)}
  </>;
}
